import React from 'react'
import { AdminCustomer } from '../../../types/api'
import { CssHandles } from '../../../hooks/useCssHandles'
import { AdminCustomerSortField } from '../../../types/adminCustomers'

type AdminCustomersTableProps = {
  customers: AdminCustomer[]
  handles: CssHandles
  showEmptyState: boolean
  renderSortIndicator: (field: AdminCustomerSortField) => React.ReactNode
  getAriaSort: (
    field: AdminCustomerSortField
  ) => 'none' | 'ascending' | 'descending'
  onSort: (field: AdminCustomerSortField) => void
  onView: (customer: AdminCustomer) => void
  onEdit: (customer: AdminCustomer) => void
  onToggleStatus: (customerId: string) => void
  formatCurrency: (value: number) => string
  formatDate: (value?: string | null) => string
}

const AdminCustomersTable: React.FC<AdminCustomersTableProps> = ({
  customers,
  handles,
  showEmptyState,
  renderSortIndicator,
  getAriaSort,
  onSort,
  onView,
  onEdit,
  onToggleStatus,
  formatCurrency,
  formatDate
}) => {
  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className={`${handles.customersTable} w-full`}>
          <thead className={`${handles.tableHeader} bg-gray-50`}>
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-6 py-3" aria-sort={getAriaSort('name')}>
                <button
                  type="button"
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Cliente
                  {renderSortIndicator('name')}
                </button>
              </th>
              <th className="px-6 py-3" aria-sort={getAriaSort('email')}>
                <button
                  type="button"
                  onClick={() => onSort('email')}
                  className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Contato
                  {renderSortIndicator('email')}
                </button>
              </th>
              <th className="px-6 py-3" aria-sort={getAriaSort('totalOrders')}>
                <button
                  type="button"
                  onClick={() => onSort('totalOrders')}
                  className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Pedidos
                  {renderSortIndicator('totalOrders')}
                </button>
              </th>
              <th className="px-6 py-3" aria-sort={getAriaSort('totalSpent')}>
                <button
                  type="button"
                  onClick={() => onSort('totalSpent')}
                  className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Total gasto
                  {renderSortIndicator('totalSpent')}
                </button>
              </th>
              <th className="px-6 py-3" aria-sort={getAriaSort('status')}>
                <button
                  type="button"
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Status
                  {renderSortIndicator('status')}
                </button>
              </th>
              <th className="sticky right-0 z-10 px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)]">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {showEmptyState ? (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className={`${handles.emptyState} space-y-3`}>
                    <p className="text-lg font-semibold text-gray-900">
                      Nenhum cliente encontrado
                    </p>
                    <p className="text-sm text-gray-500">
                      Ajuste os filtros ou tente uma nova busca.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr
                  key={customer.id}
                  className={`${handles.tableRow} transition-colors hover:bg-gray-50`}
                >
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className={`${handles.customerName} space-y-1`}>
                      <p className="text-sm font-semibold text-gray-900">
                        {customer.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        CPF: {customer.cpf ?? '—'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Registrado em {formatDate(customer.registrationDate)}
                      </p>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className={`${handles.customerContact} space-y-1`}>
                      <p className="text-sm text-gray-900">{customer.email}</p>
                      <p className="text-sm text-gray-500">
                        {customer.phone ?? '—'}
                      </p>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className={`${handles.customerMetrics} space-y-1`}>
                      <p className="text-sm font-medium text-gray-900">
                        {customer.totalOrders} pedidos
                      </p>
                      <p className="text-xs text-gray-500">
                        Último em {formatDate(customer.lastOrderDate)}
                      </p>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <p className="text-sm font-semibold text-blue-600">
                      {formatCurrency(customer.totalSpent)}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`${handles.statusBadge} inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        customer.isActive
                          ? 'bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                          : 'bg-red-100 text-red-700 ring-1 ring-inset ring-red-200'
                      }`}
                    >
                      {customer.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="sticky right-0 z-10 bg-white px-6 py-4">
                    <div
                      className={`${handles.customerActions} flex items-center justify-end gap-2`}
                    >
                      <button
                        type="button"
                        onClick={() => onView(customer)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        aria-label={`Ver detalhes de ${customer.name}`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(customer)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        aria-label={`Editar ${customer.name}`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleStatus(customer.id)}
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                          customer.isActive
                            ? 'border-amber-200 bg-amber-50 text-amber-600 hover:border-amber-300 hover:bg-amber-100'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-100'
                        }`}
                        aria-label={
                          customer.isActive
                            ? `Desativar ${customer.name}`
                            : `Ativar ${customer.name}`
                        }
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 2v10" />
                          <path d="M4.93 4.93a10 10 0 1 0 14.14 0" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminCustomersTable

